import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFinance } from "./FinanceContext";
import { sum, sumTo } from "./data/model";
import { SCENARIOS } from "./data/seedData";
import type { Scenario } from "./data/types";
import { AnnualBar } from "./components/AnnualBar";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import { ForecastChart } from "./components/charts/ForecastChart";
import { toneColor } from "./components/tone";

const SCENARIO_NOTE: Record<string, string> = {
  Approved: "Board-approved baseline",
  Forecast: "Run-rate projection",
  "Revised Budget": "Mid-cycle revision",
};

export function ForecastPage() {
  const { model, fmt, selMonth, scenario } = useFinance();
  const base = model.items.reduce((s, i) => s + sum(i.planned), 0);
  const ytdA = model.items.reduce((s, i) => s + sumTo(i.actual, selMonth), 0);
  const fc = Math.round((ytdA / (selMonth + 1)) * 12);
  const approved = model.items.reduce((s, i) => s + i.approved, 0);
  const rows = (Object.keys(SCENARIOS) as Scenario[]).map((s) => ({
    s,
    total: Math.round(base * SCENARIOS[s]),
    vs: Math.round(base * SCENARIOS[s]) - approved,
  }));

  const kpis: KpiItem[] = [
    { label: "Approved Budget", value: fmt(approved) },
    { label: "Run-rate Forecast", value: fmt(fc) },
    {
      label: "Forecast vs Approved",
      value: fmt(fc - approved),
      tone: approved - fc >= 0 ? "pos" : "neg",
    },
    {
      label: "Projected Utilization",
      value: (approved ? (fc / approved) * 100 : 0).toFixed(1) + "%",
    },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Forecast &amp; Scenario Planning
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Scenarios: Approved · Revised Budget · Forecast — active: <strong>{scenario}</strong>{" "}
        (switch in the toolbar above)
      </Typography>
      <AnnualBar />
      <KpiGrid items={kpis} />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Forecast vs Approved
        </Typography>
        <ForecastChart approved={approved} fc={fc} />
      </Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scenario</TableCell>
              <TableCell>Factor</TableCell>
              <TableCell align="right">Annual Budget</TableCell>
              <TableCell align="right">vs Approved</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.s}>
                <TableCell sx={{ fontWeight: r.s === scenario ? 800 : 400 }}>
                  {r.s}
                  {r.s === scenario ? <Chip size="small" label="active" sx={{ ml: 1 }} /> : null}
                </TableCell>
                <TableCell>×{SCENARIOS[r.s]}</TableCell>
                <TableCell align="right">{fmt(r.total)}</TableCell>
                <TableCell align="right" sx={{ color: toneColor(r.vs <= 0 ? "pos" : "neg") }}>
                  {fmt(r.vs)}
                </TableCell>
                <TableCell>{SCENARIO_NOTE[r.s] ?? ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
